// Models
import { DependencyExtensionKeys } from "../../../service/planner/entities/IDependencyResource";
import { createIdentitySet } from "../../../utilities/identity";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
// Utilities
import cloneDeep from "lodash/cloneDeep";
import { Temporal } from "temporal-polyfill-lite";
/** Default archival info used when none is provided to the Dependency constructor. */ export const defaultArchivalInfo = {
    isArchived: false,
    justification: null,
    modifiedBy: null,
    modifiedDate: null
};
/** Returns a fresh default creation info with an empty-identity stub. Used when none is provided to the Dependency constructor. */ export const defaultCreationInfo = ()=>({
        createdBy: {
            user: {
                id: "",
                tenantId: ""
            },
            application: {
                id: "",
                tenantId: ""
            }
        },
        createdDateTime: Temporal.Now.instant(),
        externalId: null,
        externalVersion: null,
        scenarioId: null,
        scope: null,
        externalUrl: null
    });
/**
 * The dependency entity implementation.
 */ export class Dependency extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed, source) {
        return {
            constraint: changed.constraint,
            extensions: changed.extensions && this.serializeExtensions()
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            archivalInfo: (value)=>({
                    ...value,
                    modifiedDate: value.modifiedDate ? Temporal.PlainDate.from(value.modifiedDate) : null
                }),
            creationInfo: (value)=>({
                    ...value,
                    createdDateTime: Temporal.Instant.from(value.createdDateTime)
                }),
            extensions: Dependency.deserializeExtensions
        });
    }
    static fromResource(resource) {
        return new Dependency({
            id: resource.id,
            version: resource["@etag"],
            archivalInfo: {
                ...resource.archivalInfo,
                modifiedBy: resource.archivalInfo.modifiedBy ? createIdentitySet(resource.archivalInfo.modifiedBy) : null,
                modifiedDate: resource.archivalInfo.modifiedDate ? Temporal.PlainDate.from(resource.archivalInfo.modifiedDate) : null
            },
            creationInfo: {
                ...resource.creationInfo,
                createdBy: createIdentitySet(resource.creationInfo.createdBy),
                createdDateTime: Temporal.Instant.from(resource.creationInfo.createdDateTime)
            },
            // planId is a client-only field; entities produced from a service resource are unstamped
            planId: "",
            sourceItemId: resource.sourceItemId,
            sourceItemType: resource.sourceItemType,
            targetItemId: resource.targetItemId,
            targetItemType: resource.targetItemType,
            constraint: resource.constraint,
            extensions: this.deserializeExtensions(resource.extensions)
        });
    }
    static fromPersistedResource(resource) {
        return this.fromResource(resource).withPlanId(resource.planId);
    }
    static deserializeExtensions(extensions) {
        const deserializedExtensions = {};
        for(const key in DependencyExtensionKeys){
            if (extensions.hasOwnProperty(key)) {
                deserializedExtensions[key] = JSON.parse(extensions[key]);
            }
        }
        return deserializedExtensions;
    }
    getClone() {
        return new Dependency(this.cloneProps());
    }
    /**
     * Returns a clone of this dependency with the supplied planId stamped onto it
     */ withPlanId(planId) {
        return new Dependency({
            ...this.cloneProps(),
            planId
        });
    }
    cloneProps() {
        return {
            id: this.id,
            version: this.version,
            archivalInfo: {
                ...cloneDeep(this.archivalInfo),
                modifiedDate: this.archivalInfo.modifiedDate ? Temporal.PlainDate.from(this.archivalInfo.modifiedDate) : null
            },
            creationInfo: {
                ...cloneDeep(this.creationInfo),
                createdDateTime: Temporal.Instant.from(this.creationInfo.createdDateTime)
            },
            planId: this.planId,
            sourceItemId: this.sourceItemId,
            sourceItemType: this.sourceItemType,
            targetItemId: this.targetItemId,
            targetItemType: this.targetItemType,
            constraint: this.constraint,
            extensions: {
                ...this.extensions
            }
        };
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            archivalInfo: {
                isArchived: this.archivalInfo.isArchived,
                justification: this.archivalInfo.justification,
                modifiedBy: this.archivalInfo.modifiedBy,
                modifiedDate: this.archivalInfo.modifiedDate?.toString() ?? null
            },
            creationInfo: {
                createdBy: this.creationInfo.createdBy,
                createdDateTime: this.creationInfo.createdDateTime.toString(),
                externalId: this.creationInfo.externalId,
                externalVersion: this.creationInfo.externalVersion,
                scenarioId: this.creationInfo.scenarioId,
                scope: this.creationInfo.scope,
                externalUrl: this.creationInfo.externalUrl
            },
            planId: this.planId,
            sourceItemId: this.sourceItemId,
            sourceItemType: this.sourceItemType,
            targetItemId: this.targetItemId,
            targetItemType: this.targetItemType,
            constraint: this.constraint,
            extensions: this.serializeExtensions()
        };
    }
    forCreate() {
        return {
            sourceItemId: this.sourceItemId,
            sourceItemType: this.sourceItemType,
            targetItemId: this.targetItemId,
            targetItemType: this.targetItemType,
            constraint: this.constraint,
            extensions: this.serializeExtensions()
        };
    }
    /**
     * Serializes the extensions of the dependency.
     * @returns A record of serialized extensions.
     */ serializeExtensions() {
        const serializedExtensions = {};
        for(const key in DependencyExtensionKeys){
            if (this.extensions.hasOwnProperty(key)) {
                serializedExtensions[key] = JSON.stringify(this.extensions[key]);
            }
        }
        return serializedExtensions;
    }
    constructor(propertyBag){
        super(propertyBag);
        this.archivalInfo = propertyBag.archivalInfo ?? defaultArchivalInfo;
        this.creationInfo = propertyBag.creationInfo ?? defaultCreationInfo();
        this.planId = propertyBag.planId;
        this.sourceItemId = propertyBag.sourceItemId;
        this.sourceItemType = propertyBag.sourceItemType;
        this.targetItemId = propertyBag.targetItemId;
        this.targetItemType = propertyBag.targetItemType;
        this.constraint = propertyBag.constraint;
        this.extensions = propertyBag.extensions ?? {};
    }
}
